"use client";

import { useEffect, useRef } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { useBillingState } from "@/hooks/useBillingState";
import { identifyClientUser, resetClientAnalytics, captureClientEvent } from "@/lib/analytics/client";

export function PosthogIdentify() {
  const { billing } = useBillingState();
  const user = useQuery(api.users.getMyInfo);
  const onboarding = useQuery(api.onboarding.getMyOnboarding);
  const trackCreatorPurchase = useAction(api.referrals.trackCreatorPlanPurchase);
  const lastIdentifiedRef = useRef<string | null>(null);
  const lastIdentifyHashRef = useRef<string | null>(null);
  const lastProductIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    const userId = user?._id as string | undefined;

    if (userId) {
      const properties: Record<string, unknown> = {
        email: user?.email ?? undefined,
        products: billing?.planIds ?? [],
      };

      if (onboarding) {
        properties.onboardingCompleted = onboarding.completed ?? false;
        if (onboarding.aLevelYear) properties.aLevelYear = onboarding.aLevelYear;
        if (onboarding.sitting) properties.sitting = onboarding.sitting;
        if (onboarding.tmuaUnis?.length) properties.tmuaUnis = onboarding.tmuaUnis;
        if (typeof onboarding.confidence === "number") properties.confidence = onboarding.confidence;
      }

      const identifyHash = JSON.stringify({ id: userId, properties });
      if (lastIdentifyHashRef.current !== identifyHash) {
        identifyClientUser(userId, properties);
        lastIdentifyHashRef.current = identifyHash;
        if (lastIdentifiedRef.current !== userId) {
          captureClientEvent("auth_signed_in", { source: "convex_user" });
          lastIdentifiedRef.current = userId;
        }
      }

      const productIds: string[] = billing?.planIds ?? [];
      const prevProductIds = lastProductIdsRef.current;
      if (prevProductIds !== null) {
        const newProducts = productIds.filter((id) => !prevProductIds.includes(id));
        if (newProducts.length > 0) {
          captureClientEvent("plan_purchase_detected", {
            userId,
            newProducts,
            allProducts: productIds,
          });
          if (process.env.NODE_ENV === "development") {
            console.debug("PosthogIdentify: tracking creator purchase", {
              newProducts,
              allProducts: productIds,
            });
          }
          void trackCreatorPurchase({ productIds: newProducts })
            .then((result) => {
              if (process.env.NODE_ENV === "development") {
                console.debug("PosthogIdentify: creator purchase result", result);
              }
            })
            .catch((error: unknown) => {
              if (process.env.NODE_ENV === "development") {
                console.warn("PosthogIdentify: failed to track creator purchase", error);
              }
            });
        }
      }
      lastProductIdsRef.current = productIds;

      if (typeof window !== "undefined") {
        try {
          const now = Date.now();
          const lastActiveKey = `ph:lastActive:${userId}`;
          const lastEventKey = `ph:lastActivityEventAt:${userId}`;
          const lastActiveRaw = window.localStorage.getItem(lastActiveKey);
          const lastEventRaw = window.localStorage.getItem(lastEventKey);
          const lastActive = lastActiveRaw ? Number(lastActiveRaw) : undefined;
          const lastEvent = lastEventRaw ? Number(lastEventRaw) : undefined;
          const diffDays = lastActive ? (now - lastActive) / (1000 * 60 * 60 * 24) : undefined;

          let category: "new" | "returning" | "resurrecting" | "dormant" = "new";
          if (diffDays !== undefined) {
            if (diffDays <= 7) {
              category = "returning";
            } else if (diffDays <= 28) {
              category = "resurrecting";
            } else {
              category = "dormant";
            }
          }

          const shouldEmit = !lastEvent || now - lastEvent > 6 * 60 * 60 * 1000;
          if (shouldEmit) {
            captureClientEvent("user_activity_classified", {
              userId,
              category,
              lastActiveDaysAgo: diffDays ?? null,
            });
            window.localStorage.setItem(lastEventKey, String(now));
          }
          window.localStorage.setItem(lastActiveKey, String(now));
        } catch (error) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Failed to track activity classification", error);
          }
        }
      }
    } else if (user === null && lastIdentifiedRef.current) {
      resetClientAnalytics();
      lastIdentifiedRef.current = null;
      lastIdentifyHashRef.current = null;
      lastProductIdsRef.current = null;
    }
  }, [user, billing, onboarding, trackCreatorPurchase]);

  return null;
}
